import type { Scale } from '@tunasong/schemas'
import { useSyncExternalStore } from 'react'

/**
 * The song-key writer, reachable by the song's id (song key K3, #2750, plan D2). The song header registers it; the
 * section header, Section properties and the Set Scale command call `propagate` after an explicit section key, and
 * Settings shows `suggestion`. None of them import the song plugin, and Settings has no editor handle, only the song.
 */
export interface SongKeyBridge {
  /** What Settings shows above the key field (spec §7); null when there is nothing to use */
  suggestion: { text: string; use: Scale } | null
  /** An explicitly set section key fills an empty song key, with Undo (spec §3). `from` names the section */
  propagate(scale: Scale, from?: string): void
  /** Set the song key, with the header's snackbar */
  set(scale: Scale | null): void
}

const bridges = new Map<string, SongKeyBridge>()
const listeners = new Set<() => void>()

const notify = () => listeners.forEach(listener => listener())

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export const getSongKeyBridge = (songId: string | undefined) => (songId ? bridges.get(songId) : undefined)

/** Register a song's bridge; the returned function removes it only while it is still the registered one */
export const registerSongKeyBridge = (songId: string, bridge: SongKeyBridge) => {
  bridges.set(songId, bridge)
  notify()
  return () => {
    if (bridges.get(songId) === bridge) {
      bridges.delete(songId)
      notify()
    }
  }
}

export const useSongKeyBridge = (songId: string | undefined) =>
  useSyncExternalStore(subscribe, () => getSongKeyBridge(songId))
